import { useState, type FormEvent } from "react";
import { useAuth } from "../auth/AuthProvider";
import { useI18n } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/messages";

/** Second step of a password sign-in when the account has two-factor authentication on. */
export function TwoFactorStep({ onDone }: { onDone: () => void }) {
  const { t } = useI18n();
  const auth = useAuth();
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState(false);
  const [trust, setTrust] = useState(false);
  const [error, setError] = useState<MessageKey | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const r = await auth.verifyTwoFactor(code.trim(), { backup, trustDevice: trust });
    setBusy(false);
    if (r.ok) onDone();
    else setError(r.error);
  };

  return (
    <form className="twofactor-step" onSubmit={submit} noValidate>
      <h2>{t("auth.twoFactor.title")}</h2>
      <p>{t("auth.twoFactor.needed")}</p>
      <div className="field">
        <label htmlFor="tf-code">{t(backup ? "auth.twoFactor.backupCode" : "auth.twoFactor.code")}</label>
        <input
          id="tf-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          inputMode={backup ? "text" : "numeric"}
          autoComplete="one-time-code"
          pattern={backup ? undefined : "[0-9]*"}
          maxLength={backup ? 32 : 6}
          autoFocus
          aria-invalid={Boolean(error)}
          aria-describedby={error ? "tf-error" : undefined}
        />
        {error && (
          <span id="tf-error" className="field-error">
            {t(error)}
          </span>
        )}
      </div>
      <label className="check">
        <input type="checkbox" checked={trust} onChange={(e) => setTrust(e.target.checked)} /> {t("auth.twoFactor.trust")}
      </label>
      <button type="submit" className="btn btn-primary btn-block" disabled={busy || !code.trim()}>
        {t("auth.twoFactor.submit")}
      </button>
      <button type="button" className="btn btn-link" onClick={() => (setBackup(!backup), setCode(""), setError(null))}>
        {t(backup ? "auth.twoFactor.useApp" : "auth.twoFactor.useBackup")}
      </button>
    </form>
  );
}
